import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Notification, NotificationType } from '../types';
import { generateId } from '../db/database';

interface AppContextType {
  isOnline: boolean;
  pendingMessages: number;
  setPendingMessages: (count: number) => void;
  notifications: Notification[];
  addNotification: (type: NotificationType, title: string, message: string, duration?: number) => void;
  removeNotification: (id: string) => void;
  isSidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingMessages, setPendingMessages] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      addNotification('success', 'Back Online', 'Connection restored. Syncing pending messages...');
    };

    const handleOffline = () => {
      setIsOnline(false);
      addNotification('warning', 'Offline Mode', 'You are now offline. Messages will be queued.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const addNotification = useCallback((
    type: NotificationType,
    title: string,
    message: string,
    duration = 5000
  ) => {
    const id = generateId();
    const notification: Notification = {
      id,
      type,
      title,
      message,
      duration,
      createdAt: Date.now(),
    };

    setNotifications(prev => [...prev, notification]);

    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  return (
    <AppContext.Provider
      value={{
        isOnline,
        pendingMessages,
        setPendingMessages,
        notifications,
        addNotification,
        removeNotification,
        isSidebarOpen,
        setSidebarOpen,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
